import React, { useEffect, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { CircularProgress, Modal } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./AgencyEmployeeInfoView.scss";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import CountryCodeMenuItem from "../countryCodeMenuItem/CountryCodeMenuItem";
import AgentProfilePicture from "./AgentProfilePicture";
import {
  formatDetailsDateTime,
  getCdnImageUrl,
  getImageUrl,
} from "../../../../utils/helpers/common";
import { AgentApi } from "../../../../utils/api/Agent";
import { LocalStorageHelpers } from "../../../../utils/helpers/localstorage.helpers";
import CloseIcon from "../../../../components/icons/CloseIcon";
import SearchIcon from "../../../../components/icons/SearchIcon";
import { useNavigate } from "react-router-dom";
import { clientRoutes } from "../../../../utils/routes";

const getStatusLabel = (status?: string) => {
  const s = (status || "").toLowerCase();
  if (s === "active") return "Active";
  if (s === "deactivated") return "Disabled";
  if (s === "archived") return "Archived";
  return status || "-";
};

const AgencyEmployeeInfoView = ({
  employee,
  onRefresh,
}: {
  employee: any;
  onRefresh: () => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [managerTab, setManagerTab] = useState<
    "all" | "agents" | "agency_employees"
  >("all");
  const [managerSearch, setManagerSearch] = useState("");
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerList, setManagerList] = useState<any[]>([]);
  const [selectedManager, setSelectedManager] = useState<any>(null);

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      mobile_country_code: "61",
      mobile_number: "",
      designation: "",
      reporting_manager_id: "",
      reporting_manager_role: "agency_employee",
      reporting_manager_name: "",
      profileImageLive: "",
      profileImageUploadRecord: null as any,
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("is required!"),
      last_name: Yup.string().required("is required!"),
      email: Yup.string().email("Enter valid email!").required("is required!"),
      mobile_number: Yup.string().required("is required!"),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setIsSaving(true);

      try {
        const fd = new FormData();

        fd.append("first_name", values.first_name);
        fd.append("last_name", values.last_name);
        fd.append("email", values.email);

        fd.append(
          "mobile_country_code",
          (values.mobile_country_code || "").replace("+", ""),
        );
        fd.append("mobile_number", values.mobile_number);

        fd.append("designation", values.designation || "");
        fd.append("reporting_manager_id", values.reporting_manager_id || "");
        fd.append(
          "reporting_manager_role",
          values.reporting_manager_role || "agency_employee",
        );

        if (profileImage) fd.append("profile_photo", profileImage);

        const res = await AgencyEmployeeApi.update(employee?._id, fd);

        if (res.success && res.data?.success) {
          toast.success("Agency employee updated!");

          setProfileImage(null);

          // onRefresh();

          navigate(clientRoutes.agencyEmployeeList);
        } else {
          toast.error(res?.error || res?.data?.message || "Failed to save");
        }
      } finally {
        setIsSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!employee) return;

    formik.setValues({
      first_name: employee?.first_name || "",
      last_name: employee?.last_name || "",
      email: employee?.email || "",
      mobile_country_code: employee?.mobile_country_code || "61",
      mobile_number: employee?.mobile_number || "",
      designation: employee?.designation || "",
      reporting_manager_id: employee?.reporting_manager_id || "",
      reporting_manager_role:
        employee?.reporting_manager_role || "agency_employee",
      reporting_manager_name: "",
      profileImageLive:
        employee?.profile_photo?.storage_key ||
        employee?.profile_photo?.original_file_path ||
        "",
      profileImageUploadRecord: employee?.profile_photo || null,
    });
  }, [employee?._id]);

  const discardChanges = () => {
    navigate(clientRoutes.agencyEmployeeList);
  };

  const getAgencyId = () => {
    const getData: any =
      LocalStorageHelpers?.getAgentData() ||
      LocalStorageHelpers?.getAgencyData();

    if (!getData) return "";
    if (getData?.role === "agent") return getData?.agency_ref?._id || "";
    return getData?._id || "";
  };

  const getProfileUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.medium_image_url)
      return getCdnImageUrl(uploadRecord.medium_image_url);
    if (uploadRecord.small_image_url)
      return getCdnImageUrl(uploadRecord.small_image_url);
    if (uploadRecord.storage_key)
      return getCdnImageUrl(uploadRecord.storage_key);
    if (uploadRecord.compressed_file_path)
      return getImageUrl(uploadRecord.compressed_file_path);
    if (uploadRecord.original_file_path)
      return getImageUrl(uploadRecord.original_file_path);

    return "";
  };

  const mapAgentToManager = (a: any) => ({
    id: a?._id,
    role: "agent",
    name:
      a?.fullname?.trim() ||
      `${a?.principal_agent_first_name || ""} ${a?.principal_agent_last_name || ""}`.trim() ||
      "-",
    email: a?.email || "-",
    avatar: getProfileUrl(a?.profile_image_ref),
  });

  const mapAgencyEmployeeToManager = (e: any) => ({
    id: e?._id,
    role: "agency_employee",
    name:
      e?.full_name?.trim() ||
      `${e?.first_name || ""} ${e?.last_name || ""}`.trim() ||
      "-",
    email: e?.email || "-",
    avatar: getProfileUrl(e?.profile_photo),
  });

  useEffect(() => {
    const resolve = async () => {
      const id = employee?.reporting_manager_id;
      const role = employee?.reporting_manager_role;

      if (!id || !role) {
        setSelectedManager(null);
        return;
      }

      try {
        if (role === "agency_employee") {
          const r = await AgencyEmployeeApi.getById(id);
          if (r.success && r.data?.success) {
            setSelectedManager(mapAgencyEmployeeToManager(r.data.data));
          }
        }

        if (role === "agent") {
          const agency_id = getAgencyId();
          const r = await AgentApi.getAgentList({
            page: 1,
            search: "",
            agency_id,
          });

          if (r?.data?.success) {
            const docs = r?.data?.data?.docs || [];
            const found = docs.find((x: any) => x._id === id);
            if (found) setSelectedManager(mapAgentToManager(found));
            else setSelectedManager(null);
          }
        }
      } catch (e) {
        setSelectedManager(null);
      }
    };

    resolve();
  }, [employee?.reporting_manager_id, employee?.reporting_manager_role]);

  const fetchManagers = async (searchText: string, tab = managerTab) => {
    setManagerLoading(true);

    try {
      const agency_id = getAgencyId();

      const tasks: Promise<any>[] = [];

      if (tab === "all" || tab === "agents") {
        tasks.push(
          AgentApi.getAgentList({
            page: 1,
            search: searchText,
            agency_id,
          }),
        );
      } else tasks.push(Promise.resolve(null));

      if (tab === "all" || tab === "agency_employees") {
        tasks.push(
          AgencyEmployeeApi.list({
            page: 1,
            limit: 50,
            search: searchText,
            status: "",
          }),
        );
      } else tasks.push(Promise.resolve(null));

      const [agentsRes, employeesRes] = await Promise.all(tasks);

      let agents: any[] = [];
      if (agentsRes?.data?.success) {
        agents = (agentsRes?.data?.data?.docs || []).map(mapAgentToManager);
      }

      let employees: any[] = [];
      if (employeesRes?.success && employeesRes?.data?.success) {
        employees = (employeesRes?.data?.data?.docs || []).map(
          mapAgencyEmployeeToManager,
        );
      }

      const merged =
        tab === "agents"
          ? agents
          : tab === "agency_employees"
            ? employees
            : [...agents, ...employees];

      merged.sort((x, y) => (x.name || "").localeCompare(y.name || ""));
      setManagerList(merged);
    } finally {
      setManagerLoading(false);
    }
  };

  useEffect(() => {
    if (!isManagerModalOpen) return;
    fetchManagers(managerSearch, managerTab);
  }, [isManagerModalOpen, managerTab]);

  useEffect(() => {
    if (!isManagerModalOpen) return;
    const t = setTimeout(() => fetchManagers(managerSearch, managerTab), 400);
    return () => clearTimeout(t);
  }, [managerSearch]);

  return (
    <>
      <div className="agency-employee-info-view">
        <div className="meta-row">
          <div className="meta-item">
            <p className="meta-label">Employee id</p>
            <p className="meta-value">{employee?._id || "-"}</p>
          </div>

          <div className="meta-item">
            <p className="meta-label">Status</p>
            <div
              className={`status-badge ${(employee?.status || "").toLowerCase()}`}
            >
              {getStatusLabel(employee?.status)}
            </div>
          </div>

          <div className="meta-item">
            <p className="meta-label">Created on</p>
            <p className="meta-value">
              {employee?.createdAt
                ? formatDetailsDateTime(employee.createdAt)
                : "-"}
            </p>
          </div>

          <div className="meta-item">
            <p className="meta-label">Last updated on</p>
            <p className="meta-value">
              {employee?.updatedAt
                ? formatDetailsDateTime(employee.updatedAt)
                : "-"}
            </p>
          </div>
        </div>

        <div className="section-header">
          <h3>Employee Info</h3>
          <p className="subtitle">Primary information for the employee</p>
        </div>

        <form className="form-container" onSubmit={formik.handleSubmit}>
          <div className="double-item-container">
            <div className="input-and-lable-container">
              <p className="label">First name*</p>
              <input
                className="bb-input"
                name="first_name"
                value={formik.values.first_name}
                onChange={formik.handleChange}
                placeholder="Enter first name"
              />
            </div>

            <div className="input-and-lable-container">
              <p className="label">Last name*</p>
              <input
                className="bb-input"
                name="last_name"
                value={formik.values.last_name}
                onChange={formik.handleChange}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="double-item-container">
            <div className="input-and-lable-container">
              <p className="label">Email*</p>
              <input
                className="bb-input"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                placeholder="Enter email"
              />
            </div>

            <div className="input-and-lable-container">
              <p className="label">Mobile*</p>
              <div className="unified-phone-input">
                <div className="code-picker">
                  <CountryCodeMenuItem
                    formik={formik}
                    name="mobile_country_code"
                    disabled={false}
                  />
                </div>
                <input
                  className="bb-input phone-input"
                  name="mobile_number"
                  value={formik.values.mobile_number}
                  onChange={formik.handleChange}
                  placeholder="Enter mobile"
                />
              </div>
            </div>
          </div>

          <div className="single-item-container">
            <div className="input-and-lable-container">
              <p className="label">Designation</p>
              <input
                className="bb-input"
                name="designation"
                value={formik.values.designation}
                onChange={formik.handleChange}
                placeholder="Enter designation"
              />
            </div>
          </div>

          <div className="reporting-manager-section">
            <h3>Reporting Manager</h3>
            <p className="subtitle">Manage reporting manager</p>

            <div className="rm-card">
              {selectedManager ? (
                <div className="rm-user">
                  <div className="rm-avatar">
                    {selectedManager.avatar ? (
                      <img src={selectedManager.avatar} alt="rm" />
                    ) : (
                      <div className="rm-avatar-fallback">
                        {(selectedManager.name || "U")[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="rm-user-info">
                    <span className="rm-role-badge">
                      {selectedManager.role === "agency_employee"
                        ? "Agency employee"
                        : "Agent"}
                    </span>

                    <p className="rm-name">{selectedManager.name}</p>
                    <p className="rm-email">{selectedManager.email}</p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="rm-line">
                    Role: <span>{employee?.reporting_manager_role || "-"}</span>
                  </p>

                  <p className="rm-line">
                    Id: <span>{employee?.reporting_manager_id || "-"}</span>
                  </p>
                </>
              )}

              <button
                type="button"
                className="rm-change-btn"
                onClick={() => setIsManagerModalOpen(true)}
              >
                Change reporting manager
              </button>
            </div>
          </div>

          <div className="photo-upload-section">
            <h3 className="section-title">Profile Photo</h3>
            <p className="section-subtitle">
              Preview or change the profile image
            </p>

            <AgentProfilePicture
              profileImage={profileImage}
              setProfileImage={setProfileImage}
              formik={formik}
            />
          </div>

          <div className="action-buttons">
            <button
              type="button"
              className="discard-btn"
              onClick={discardChanges}
            >
              Discard changes
            </button>

            <button className="save-btn" type="submit" disabled={isSaving}>
              {isSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Save changes"
              )}
            </button>
          </div>
        </form>
      </div>

      <Modal
        open={isManagerModalOpen}
        onClose={() => setIsManagerModalOpen(false)}
        className="reporting-manager-modal"
      >
        <div className="rm-modal-center">
          <div className="rm-modal-card">
            <div className="rm-modal-header">
              <p className="rm-title">Select reporting manager</p>

              <button
                className="rm-close"
                onClick={() => setIsManagerModalOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>

            <div className="rm-tabs">
              <button
                className={`rm-tab ${managerTab === "all" ? "active" : ""}`}
                onClick={() => setManagerTab("all")}
              >
                All
              </button>
              <button
                className={`rm-tab ${managerTab === "agents" ? "active" : ""}`}
                onClick={() => setManagerTab("agents")}
              >
                Agents
              </button>
              <button
                className={`rm-tab ${managerTab === "agency_employees" ? "active" : ""}`}
                onClick={() => setManagerTab("agency_employees")}
              >
                Agency employees
              </button>
            </div>

            <div className="rm-search">
              <div className="rm-search-icon">
                <SearchIcon />
              </div>
              <input
                placeholder="Search by name or email"
                value={managerSearch}
                onChange={(e) => setManagerSearch(e.target.value)}
              />
            </div>

            <div className="rm-list">
              {managerLoading ? (
                <div className="rm-loading">
                  <CircularProgress size={20} />
                </div>
              ) : managerList.length === 0 ? (
                <div className="rm-empty">No results</div>
              ) : (
                managerList.map((item) => (
                  <div
                    key={`${item.role}-${item.id}`}
                    className="rm-row"
                    onClick={() => {
                      formik.setFieldValue("reporting_manager_id", item.id);
                      formik.setFieldValue("reporting_manager_role", item.role);

                      setSelectedManager(item);

                      setIsManagerModalOpen(false);
                    }}
                  >
                    <div className="rm-avatar">
                      {item.avatar ? (
                        <img src={item.avatar} alt="avatar" />
                      ) : (
                        <div className="rm-avatar-fallback">
                          {(item.name || "U")[0]}
                        </div>
                      )}
                    </div>

                    <div className="rm-info">
                      <div className="rm-name-line">
                        <span className="rm-name">{item.name}</span>
                        <span className="rm-role-badge">
                          {item.role === "agency_employee"
                            ? "Agency employee"
                            : "Agent"}
                        </span>
                      </div>
                      <div className="rm-email">{item.email}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AgencyEmployeeInfoView;
